import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { User, UserState } from 'app/models/user/user.model';
import { UserAPIService } from 'app/shared/api/user.api.service';
import { Globals } from 'app/shared/globals/globals';
import { PraiseAPIService } from 'app/shared/api/praise.api.service';
import { CreatePraiseDto } from 'app/models/feedback/praise.model';
import { CompanyAPIService } from '@app/shared/api/company/company.api.service';
import { CompanyFeatures } from '@app/models/company-features.model';
import { ProfilePraiseComponent } from '@app/domain/user/component/profile-praise/profile-praise.component';
import { RoleName } from '@app/models/user-role.model';
import { OneToOneAPIService } from '@app/domain/one_to_one/api/one-to-one-api.service';
import { OneToOneScheduleDetailsView } from '@app/domain/one_to_one/model/one-to-one-schedule.model';
import { Tag } from '@app/domain/tag/model/tag.model';
import { Breadcrumb } from 'app/models/breadcrumb.model';
import { BreadcrumbService } from 'app/shared/breadcrumbs/breadcrumbs.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalComponent } from '@app/shared/modal/modal.component';
import { FrankliValidators } from '@app/shared/validators/validators';
import { forkJoin } from 'rxjs';
import { ScimUser } from '@app/models/user/scim-user.model';
import { ScimUserApiService } from '@app/shared/api/scim-user.api.service';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { SecondaryManagerAPIService } from '@app/shared/api/company/secondary-manager.api.service';
import { SecondaryManager } from '@app/models/company/company-secondary-manager/secondary-manager.model';
import { PillType } from '@app/shared/components/pill/pill.component';
import { TagType } from '@app/domain/tag/model/tag-type.model';
import { UserGoalsModalComponent } from '@app/goals/goals-components/user-goals-modal/user-goals-modal.component';
import { Site } from '@app/models/site.model';
import { CommonMessages } from '@app/constants/common.messages';
import { TranslateService } from '@ngx-translate/core';
import { SettingsMessages } from '@app/domain/settings/locale/settings.messages';
import { TagBusinessService } from '@app/domain/tag/service/tag-business.service';
import { IState } from '@app/models/state/state.model';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { TerminologyEntity } from '@app/domain/terminology/model/terminology-entity.enum';
import { ProfileMessages } from '../../locale/profile.messages';

interface PageTag extends Tag {
  selected: boolean;
}

@Component({
  selector: 'app-user-profile',
  templateUrl: 'user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {

  public readonly largeEditorMinLength = 10;
  public readonly largeEditorMaxLengthSoft = 1000;
  public readonly largeEditorMaxLengthHard = 2000;

  public readonly editorToolbar = 'undo redo | formatselect | bold italic underline strikethrough | help';

  public readonly eTerminologyEntity = TerminologyEntity;
  public readonly eSettingsMessages = SettingsMessages;
  public readonly eProfileMessages = ProfileMessages;
  public readonly eCompanyFeatures = CompanyFeatures;
  public readonly eCommonMessages = CommonMessages;
  public readonly eButtonType = ButtonType;
  public readonly eUserState = UserState;
  public readonly eRoleName = RoleName;
  public readonly ePillType = PillType;

  @ViewChild('userGoalsModal') userGoalsModal!: UserGoalsModalComponent;
  @ViewChild('praiseComp') praiseComp !: ProfilePraiseComponent;
  @ViewChild('praiseModal') praiseModal !: ModalComponent;

  id: number;
  user: User;
  userProfile: User;
  scimUser?: ScimUser;

  praiseSubmitted: boolean;

  userBirthday: string;
  titleText: string;
  workingFrom: string;

  showManagerLink: boolean;
  praiseForm!: UntypedFormGroup;
  archived: boolean;
  hasOneToOne: OneToOneScheduleDetailsView;

  availableValues: PageTag[];
  selectedValues: PageTag[];

  state: IState;
  breadcrumb: Breadcrumb;

  secondaryManager?: SecondaryManager;

  get isCurrentUserProfile(): boolean {
    if (!this.userProfile) { return false; }
    if (!this.userProfile.id) { return false; }
    if (!this.globals.user) { return false; }
    if (!this.globals.user.id) { return false; }
    return this.globals.user.id === this.userProfile.id;
  }
  
  get isManagingUserProfile(): boolean {
    if (!this.userProfile) { return false; }
    if (!this.userProfile.managerId) { return false; }
    if (!this.globals.user) { return false; }
    if (!this.globals.user.id) { return false; }
    if (this.userProfile.id === this.userProfile.managerId) { return false; }
    return this.globals.user.id === this.userProfile.managerId;
  }

  constructor(
    public globals: Globals,
    private userService: UserAPIService,
    public route: ActivatedRoute,
    private scimUserApiService: ScimUserApiService,
    private router: Router,
    private praiseAPIService: PraiseAPIService,
    private companyAPIService: CompanyAPIService,
    private oneToOneAPIService: OneToOneAPIService,
    private breadcrumbService: BreadcrumbService,
    private secondaryManagerService: SecondaryManagerAPIService,
    private formBuilder: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private notifyUtils: NotifyUtils,
    private tagBusinessService: TagBusinessService
  ) {
    this.id = undefined;
    this.user = undefined;
    this.userProfile = undefined;
    this.scimUser = undefined;

    this.praiseSubmitted = false;
    this.showManagerLink = false;

    this.availableValues = [];
    this.selectedValues = [];

    this.archived = false;

    this.userBirthday = '';
    this.titleText = '';
    this.workingFrom = '';

    this.state = {
      loading: true,
      error: false,
      errorMessage: ''
    };

    this.hasOneToOne = {} as OneToOneScheduleDetailsView;
    this.breadcrumb = this.breadcrumbService.init(this.route);
  }

  // #region - LIFECYCLE HOOKS
  ngOnInit() {
    this.getData();
  }

  ngOnDestroy() {
    this.breadcrumbService.remove(this.breadcrumb);
  }
  // #endregion

  getData() {
    this.route.paramMap.subscribe(
      (params) => {
        const id = params.get('id');

        forkJoin([
          
          this.tagBusinessService.get(null, null, [TagType.COMPANY_VALUE])
        ]).subscribe(
          ([companyValues]) => {
            // Populate company values
            this.availableValues = this.parseTagToPageTagMultiple(companyValues);

            // If no ID supplied or current users ID is supplied, get current users data
            if (!id || (+id === this.globals.user.id)) {
              return this.getCurrentUserData();
            }

            this.id = +id;

            // If ID is not a number, show error
            if (isNaN(this.id)) {
              this.doError('Invalid user ID');
              return;
            }

            this.getOtherUsersData(this.id);
          },
          (err: HttpErrorResponse) => this.doError(err.message)
        );
      }
    );

    this.initPraiseForm();
  }

  parseTagToPageTagMultiple(tags: Tag[]): PageTag[] {
    return tags.map(t => this.parseTagToPageTagSingle(t));
  }

  parseTagToPageTagSingle(tag: Tag): PageTag {
    const output = tag as PageTag;

    if (output) {
      output.selected = false;
    }

    return output;
  }

  getCurrentUserData() {
    this.breadcrumbService.updateSpecificBreadcrumb(this.breadcrumb, 'My Profile', null);
    this.populateUserDetails();
    this.getManager();

    if (this.globals.hasFeature(CompanyFeatures.AZURE_ACTIVE_DIRECTORY)) {
      this.getCurrentScimUser();
    }

    this.state.loading = false;
  }

  getCurrentScimUser() {
    this.scimUserApiService.getScimUserByUserId(this.globals.user.id).subscribe(scimUser => {
      this.scimUser = scimUser;
    });
  }

  getOtherUsersData(id: number) {
    // Get other users profile info
    this.userService.getById(id).subscribe(
      (userResponse) => {
        this.userProfile = userResponse;
        this.breadcrumbService.updateSpecificBreadcrumb(this.breadcrumb, `${userResponse.firstName} ${userResponse.lastName}'s Profile`, null);

        // Check if archived
        this.archived = this.userProfile.roles.some(role => role.name === RoleName.ARCHIVED);

        // Get 1:1s
        this.getOneToOnes();

        this.titleText = `Hey ${this.globals.user.firstName}, lets hear your ${this.globals.getTerminology(TerminologyEntity.PRAISE)} for ${this.userProfile.firstName}!`;

        this.userBirthday = (new Date(this.userProfile.dateOfBirth!).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
        }));

        this.getManager();
        this.checkSecondaryManager();

        this.state.loading = false;
      },
      (err: HttpErrorResponse) => this.doError(err.message)
    );
  }

  getOneToOnes(){
    this.oneToOneAPIService.getOneToOneSchedulesForManagerMe().subscribe(
      (success: OneToOneScheduleDetailsView[]) => {
        this.hasOneToOne = success.find(x => x.participants.length === 1 && x.participants[0].id === this.userProfile.id && x.status !== 'ARCHIVED')!;
        if (!this.hasOneToOne) {
          this.oneToOneAPIService.getOneToOneSchedulesForUserMe().subscribe(resp => {
            this.hasOneToOne = resp.find(x => x.manager.id === this.userProfile.id && x.status !== 'ARCHIVED')!;
          });
        }
      }
    );
  }

  checkSecondaryManager(){
    if(!this.globals.hasFeature(CompanyFeatures.SECONDARY_MANAGER) || !this.globals.hasRole(RoleName.SECONDARY_MANAGER)){
      return;
    }

    this.secondaryManagerService.getSecondaryManagerForUser(this.userProfile.id, this.globals.user.id).subscribe(x => {
      if (!x) {
        return;
      }

      this.secondaryManager = x;
    });
  }

  getManager(): void {
    if (+this.userProfile.managerId === +this.userProfile.id) {
      this.showManagerLink = false;
      this.userProfile.managerName = 'No Manager';
    } else if (this.userProfile.managerId !== null) {
      this.userService.getById(this.userProfile.managerId).subscribe(manager => {
        this.showManagerLink = (manager.userState !== 'INVITED');
        this.userProfile.managerName = manager.firstName + ' ' + manager.lastName;
        this.userProfile.managerId = manager.id;
      });
    } else {
      this.showManagerLink = false;
    }
  }

  getPraise(): void {
    this.praiseComp.getPraise();
  }

  private populateUserDetails(): void {
    this.userProfile = this.globals.user;

    // making the date show up as Month Date
    this.userBirthday = (new Date(this.userProfile.dateOfBirth!).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    }));
  }

  toggleValue(value: PageTag) {
    value.selected = !value.selected;
  }

  createOneToOne() {
    this.router.navigate(
      ['/one-to-one/create'],
      {
        queryParams: {
          userId: this.id
        }
      }
    );
  }

  // #region - PROFILE PRAISE
  initPraiseForm() {
    this.praiseForm = this.formBuilder.group({
      praise: new UntypedFormControl('', [Validators.required, FrankliValidators.softMinValidation(this.largeEditorMinLength), FrankliValidators.softMaxValidation(this.largeEditorMaxLengthSoft), Validators.maxLength(this.largeEditorMaxLengthHard)]),
    });

    this.praiseForm.controls.praise.valueChanges.subscribe(() => {
      this.cdRef.detectChanges();
    });
  }

  startPraise(): void {
    this.initPraiseForm();
    this.praiseSubmitted = false;
    this.praiseModal.show();
  }

  cancelPraise(hideModal = true): void {
    this.praiseSubmitted = false;
    this.praiseForm.reset();

    if (hideModal) {
      this.praiseModal.hide();
    }
  }

  confirmPraise() {
    this.praiseSubmitted = true;
    if (this.praiseForm.valid) {
      const praise: CreatePraiseDto = {
        recipientId: this.userProfile.id,
        message: this.praiseForm.controls.praise.value,
        values: this.availableValues.filter(av => av.selected)
      };
      this.praiseAPIService.submitPraise(praise).subscribe(() => {
        this.notifyUtils.notifyIcon({
          icon: 'fal fa-thumbs-up',
          message: 'Praise successfully posted',
        }, {
          type: 'frankli-blue',
          placement: {
            from: 'top',
            align: 'right',
          },
        });
        this.praiseSubmitted = false;
        this.praiseForm.controls.praise.setValue('');
        this.availableValues.forEach(av => av.selected = false);
        this.getPraise();
        this.praiseModal.hide();
      });
    }
  }

  // #endregion

  doError(message: string) {
    this.state.loading = false;
    this.state.error = true;
    this.state.errorMessage = message;
  }

  compareSites(siteA: Site, siteB: Site): boolean {
    if (!siteA && !siteB) {
      return true;
    }
    if (!siteA) {
      return false;
    }
    if (!siteB) {
      return false;
    }
    return siteA.id === siteB.id;
  }

  onUserUpdated(user: User): void {
    this.globals.user = user;
    this.userProfile = user;
    this.getManager();
  }
}
